import React, { useEffect, useState } from 'react';
import Layout from '~/components/Layout';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-lodash-debounce'
import { RouteTypes } from '~/routes/constants';
import dayjs from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat'
import i18n from '~/components/i18n'
import 'dayjs/locale/de';
import { StyledTab } from '~/styles/table';
import TradingActivities from './TradingActivities';
import TableComponent from '~/components/TableComponent';
import { useNavigate } from 'react-router-dom';
import { StockExchangeQuery } from '~/QueryHooks/StockExchangeQuery';
import TermsModal from '~/components/TermsModal';
import { Input } from 'semantic-ui-react';
import { FixedPricePrefixType } from '~/api/services/dashboard';

const getFarmerActions = () => [
  {
    popupMessage: i18n.t('Show more'),
    type: 'icon',
    name: 'eye',
    size: 'large',
    actionType: 'milk-farmer-actions-show',
    link: RouteTypes.STOCK_EXCHANGE_SHOW
  },
  {
    popupMessage: i18n.t('Offer amount'),
    type: 'icon',
    name: 'reply all',
    size: 'large',
    actionType: 'milk-farmer-actions-amount',
    link: RouteTypes.STOCK_EXCHANGE_OFFER_AMOUNT,
    display: ['active']
  },
  {
    popupMessage: i18n.t('Results'),
    type: 'icon',
    name: 'clipboard outline',
    size: 'large',
    actionType: 'milk-farmer-actions-results',
    link: RouteTypes.STOCK_EXCHANGE_RESULTS,
    display: ['active', 'completed', 'examination']
  }
]

const getDefaultTableColumnHeaders = () => [
  {
    name: i18n.t('Status'),
    key: 'statusLabel'
  },
  {
    name: i18n.t('Trading slot ID'),
    key: 'uniqueId'
  },
  {
    name: i18n.t('Start/End'),
    key: 'dateFrom',
    type: 'startEnd',
    sorted: 'desc'
  },
  {
    name: i18n.t('Actions')
  }
]

const StockExchange: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 30,
    search: '',
    status: 'actual'
  })
  const farmerActions = getFarmerActions()
  const [acceptTermsRow, setAcceptTermsRow] = useState(null)
  const [link, setLink] = useState('')
  const [search, setSearch] = useState('')
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const navigate = useNavigate()
  const debouncedValue = useDebounce(search, 300)
  const [dataFilterActivities, setDataFilterActivities] = useState({
    page: 0,
    status: 'completed',
    rows: 10
  })
  const [tableColumnHeaders, setTableColumnHeaders] = useState(getDefaultTableColumnHeaders())

  const {
    isLoading: isBusyData,
    data: stockExchangeData,
    isFetching: isFetchingData,
    refetch: fetchFilteredData
  } = StockExchangeQuery.getMilkFixexPriceList({ ...dataFiltered, page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1 })

  const {
    data: stockExchangeOptions,
    isLoading: isBusyPriceList,
    refetch: fetchOptions
  } = StockExchangeQuery.getMilkFixexPriceListOptions()

  const {
    data: tradingActivityOptions,
    isLoading: isBusyActivity
  } = StockExchangeQuery.getTradingActivityOptions()

  const {
    data: tradingSettings,
    isLoading: isBusySettings
  } = StockExchangeQuery.getFarmerSettingsMilk()

  const {
    data: stockExchangeCount,
    isLoading: isBusyPriceCount,
    refetch: fetchCount
  } = StockExchangeQuery.getMilkFixexPriceCount({ ...dataFiltered, page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1 })

  const {
    data: tradingActivitiesData,
    isLoading: isBusyTrading,
    isFetching: isFetchingTrading,
    refetch: fetchTradingActivities
  } = StockExchangeQuery.getTradingActivities({ ...dataFilterActivities, page: dataFilterActivities.page === 0 ? dataFilterActivities.page : dataFilterActivities.page - 1 })

  useEffect(() => {
    dayjs.extend(LocalizedFormat)
    setTableColumnHeaders(getDefaultTableColumnHeaders())
    fetchOptions()
    fetchFilteredData()
  }, [t])

  useEffect(() => {
    if (dataFilterActivities) {
      fetchTradingActivities()
    }
  }, [dataFilterActivities])

  useEffect(() => {
    if (dataFiltered) {
      fetchFilteredData()
      fetchCount()
    }
  }, [dataFiltered])

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedValue, page: 0 })
  }, [debouncedValue])

  function renderSearch() {
    return <Input
      icon={{ name: 'search', circular: true, link: true }}
      placeholder={t('Search...')}
      value={search}
      onChange={(e, data) => setSearch(data.value)}
    />
  }

  function handleClickAction(row, type) {
    const action = farmerActions.find(action => action.actionType === type)
    const displayTradingRoom = Boolean(tradingSettings?.tradingRoom) && !row.termsAccepted && tradingSettings?.terms?.length > 0
    if (displayTradingRoom) {
      setAcceptTermsRow(row)
      setLink(action.link.replace(':id', row.id))
    } else {
      navigate(action.link.replace(':id', row.id))
    }
  }

  function renderContent() {
    return <TableComponent
      actions={farmerActions}
      isFetchingData={isFetchingData}
      animated
      tableColumnHeaders={tableColumnHeaders}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setTableColumnHeaders(tableColumnHeaders)
        setDataFiltered(data)
      }}
      handleClickAction={handleClickAction}
      rows={stockExchangeData?.rows || []}
      total={stockExchangeData?.filtered || 0}
    />
  }

  function handleTabChange(activeIndex) {
    if (!isFetchingData) {
      const status = stockExchangeOptions.find((_, indexOption) => indexOption === activeIndex)
      setActiveTabIndex(activeIndex)
      setDataFiltered({
        ...dataFiltered,
        status: status.key,
        page: 0
      })
    }
  }

  function renderPanes() {
    const panes = stockExchangeOptions.map(option => {
      const count = stockExchangeCount.find(count => count.status === option.key)
      return {
        menuItem: `${option.label} (${count?.count || 0})`,
        render: () => renderContent()
      }
    })

    return <StyledTab
      menu={{ secondary: true, pointing: true }}
      panes={panes}
      activeIndex={activeTabIndex}
      onTabChange={(_, data) => handleTabChange(data.activeIndex)}
    />
  }

  const loading = isBusyPriceCount || isBusyPriceList || isBusyData || isBusyTrading || isBusySettings

  const stockExchangeDataReady = stockExchangeOptions && stockExchangeCount && stockExchangeData

  const tradingActivitieDataReady = tradingActivitiesData && !isFetchingData && tradingActivityOptions

  return <Layout
    isBusy={loading}
    segmented
    title={t('Stock exchange')}
    filters={renderSearch()}
  >
    <TermsModal
      tradingSlotId={acceptTermsRow?.id}
      openTermsModal={Boolean(acceptTermsRow)}
      link={link}
      prefix={FixedPricePrefixType.StockExchange}
      settingsData={tradingSettings}
      handleClose={() => {
        setAcceptTermsRow(null)
        setLink('')
      }}
    />
    {stockExchangeDataReady && renderPanes()}
    {tradingActivitieDataReady && <TradingActivities
      tradingActivities={tradingActivitiesData}
      isFetchingData={isFetchingTrading}
      tradingOptions={tradingActivityOptions}
      dataFilterActivities={dataFilterActivities}
      onChange={(value) => setDataFilterActivities(value)}
    />}
  </Layout>;
};

export default StockExchange;
